import { useState } from 'react'
import { DateTime, Duration } from 'luxon'
import { CircularProgress } from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'
import useSound from 'use-sound'
import { useTranslation } from 'react-i18next'

import { FeatureFlag, useFeatureFlags } from 'src/hooks/use-feature-flags'
import { useInterval } from 'src/lib/hooks/use-interval'
import { useMatches, useTriggerAutomatch } from 'src/hooks/use-matches'
import { usePrivateProfile } from 'src/hooks/use-profile'
import { IconButton } from 'src/system/ui/button/IconButton'
import awpReload from 'src/assets/sounds/awp-reload.mp3'
import noAmmo from 'src/assets/sounds/no-ammo.mp3'

const reloadDuration = Duration.fromObject({ minutes: 1 })
const refreshRate = Duration.fromObject({ milliseconds: 100 }).as(
  'milliseconds'
)

export function CheckForMatchesButton(props: {
  cooldown: number
  resetCooldown: () => void
}) {
  const [reloadButtonEnabled] = useFeatureFlags([FeatureFlag.reloadButton])
  const { t } = useTranslation()

  if (!reloadButtonEnabled) {
    return null
  }

  if (props.cooldown === 0) {
    return (
      <ReloadButton
        aria-label={t('matches:reload')}
        resetCooldown={props.resetCooldown}
      />
    )
  }

  return <CooldownButton aria-label={t('matches:cooldown')} />
}

function ReloadButton(props: {
  'aria-label'?: string
  resetCooldown: () => void
}) {
  const [play] = useSound(awpReload)
  const { refetch: refetchMatches } = useMatches()
  const { refetch: refetchPrivateProfile } = usePrivateProfile()
  const { trigger } = useTriggerAutomatch()
  const handleClick = () => {
    props.resetCooldown()
    trigger('reload_button')
    refetchPrivateProfile()
    refetchMatches()
    play()
  }
  return (
    <IconButton aria-label={props['aria-label']} onClick={handleClick}>
      <RefreshIcon />
    </IconButton>
  )
}

// While the reload is on cooldown, show an indeterminate spinner — a plain
// "working on it" indicator, not a slow-filling progress ring (there's no real
// completion event to track; the button just becomes clickable again after the
// window). Clicking still plays the no-ammo sound and does nothing.
function CooldownButton(props: { 'aria-label'?: string }) {
  const [play] = useSound(noAmmo)
  return (
    <IconButton aria-label={props['aria-label']} onClick={() => play()}>
      <CircularProgress
        size='18px'
        // Slow the spin — MUI's default 1.4s indeterminate cycle reads as frantic
        // at this size. Slow the rotation and the arc dash together to match.
        sx={{
          animationDuration: '5s',
          '& .MuiCircularProgress-circle': { animationDuration: '5s' }
        }}
      />
    </IconButton>
  )
}

// Remaining cooldown as a 0→1 fraction, decaying linearly over reloadDuration from
// `anchor`: 1 = just checked, 0 = cooled down. Pure so it's unit-testable.
export function cooldownProgress(
  anchor: DateTime | null,
  now: DateTime = DateTime.local()
): number {
  if (anchor == null) return 0
  const sinceAnchor = now.diff(anchor).as('milliseconds')
  return Math.min(
    1,
    Math.max(0, 1 - sinceAnchor / reloadDuration.as('milliseconds'))
  )
}

// The later of two (nullable) instants, or whichever is non-null.
export function latestDateTime(
  a: DateTime | null,
  b: DateTime | null
): DateTime | null {
  if (a == null) return b
  if (b == null) return a
  return a > b ? a : b
}

export function useCooldown(checkedAt?: DateTime | null) {
  const [reloadedAt, setReloadedAt] = useState<DateTime | null>(null)
  const [cooldown, setCooldown] = useState(0)

  const resetCooldown = () => setReloadedAt(DateTime.local())

  // Anchor on the later of the server's last check (survives reload / other devices /
  // background checks) and the click just made (instant feedback). The 1-min window
  // matches the server gate, so the button never looks ready while a trigger'd fail.
  const anchor = latestDateTime(checkedAt ?? null, reloadedAt)

  useInterval(() => {
    const next = cooldownProgress(anchor)
    // Only touch state while a cooldown is live (plus the tick that clears an
    // active one): an idle control must not schedule updates 10×/s.
    if (next === 0 && cooldown === 0) return
    setCooldown(next)
  }, refreshRate)

  return { cooldown, resetCooldown }
}
